import {ChangeDetectionStrategy, Component, computed, effect, inject, input, signal} from '@angular/core';
import {ApartmentService} from "../../services/apartment.service";
import {AsyncPipe, CommonModule, DatePipe} from "@angular/common";
import {LabeledComponent} from "../../components/labeled/labeled.component";
import {MessageService, PrimeTemplate} from "primeng/api";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {TableModule} from "primeng/table";
import {TypedTableRowDirective} from "../../typed-table-row.directive";
import {ButtonModule} from 'primeng/button';
import {LotteryDetailDto, LotteryPhotoUploadTarget, UploadLotteryPhotoCommand} from 'admin-api-dto';
import {finalize, from, switchMap} from 'rxjs';

@Component({
  selector: 'app-apartment-page',
  imports: [
    AsyncPipe,
    CommonModule,
    DatePipe,
    FormsModule,
    LabeledComponent,
    PrimeTemplate,
    ReactiveFormsModule,
    TableModule,
    TypedTableRowDirective,
    ButtonModule,
  ],
  templateUrl: './apartment-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApartmentPageComponent {
  public readonly apartmentId = input.required<string>();

  private readonly apartmentService = inject(ApartmentService);
  private readonly messageService = inject(MessageService);

  public readonly apartment$ = computed(
    () => this.apartmentService.getApartment(this.apartmentId())
  );

  protected readonly detail = signal<LotteryDetailDto | undefined>(undefined);
  protected readonly loadingPhotos = signal(false);
  protected readonly actionInFlight = signal(false);
  protected readonly uploadFileName = signal('');
  protected readonly uploadFile = signal<File | undefined>(undefined);
  protected readonly uploadTarget = signal<LotteryPhotoUploadTarget | undefined>(undefined);
  protected readonly uploadPreparing = signal(false);

  constructor() {
    effect(() => {
      const id = this.apartmentId();
      if (id) {
        this.loadDetail(id);
      }
    });
  }

  protected refreshPhotos() {
    this.loadDetail(this.apartmentId());
  }

  protected movePhoto(index: number, direction: 'up' | 'down') {
    const detail = this.detail();
    if (!detail || this.actionInFlight()) {
      return;
    }

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= detail.photos.length) {
      return;
    }

    const reordered = [...detail.photos];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];

    this.actionInFlight.set(true);
    this.apartmentService
      .reorderLotteryPhotos(this.apartmentId(), {photos: reordered})
      .pipe(finalize(() => this.actionInFlight.set(false)))
      .subscribe({
        next: updated => {
          this.detail.set(updated);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to reorder photos',
            detail: 'Please try again.',
          });
        },
      });
  }

  protected setAsMain(src: string) {
    if (this.actionInFlight()) {
      return;
    }

    this.actionInFlight.set(true);
    this.apartmentService
      .setMainLotteryPhoto(this.apartmentId(), {src})
      .pipe(finalize(() => this.actionInFlight.set(false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Main image updated',
          });
          this.loadDetail(this.apartmentId());
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to update main image',
            detail: 'Please try again.',
          });
        },
      });
  }

  protected deletePhoto(src: string) {
    if (this.actionInFlight()) {
      return;
    }

    this.actionInFlight.set(true);
    this.apartmentService
      .deleteLotteryPhoto(this.apartmentId(), {src})
      .pipe(finalize(() => this.actionInFlight.set(false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Photo removed',
          });
          this.loadDetail(this.apartmentId());
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to delete photo',
            detail: 'Please try again.',
          });
        },
      });
  }

  protected uploadPhoto() {
    const detail = this.detail();
    const file = this.uploadFile();
    const target = this.uploadTarget();
    if (!detail || !file || !target || this.actionInFlight()) {
      return;
    }

    const apartmentId = this.apartmentId();
    this.actionInFlight.set(true);
    from(
      fetch(target.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': target.contentType,
        },
      })
    )
      .pipe(
        switchMap(response => {
          if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
          }
          const command: UploadLotteryPhotoCommand = {
            key: target.key,
            alt: this.uploadFileName().trim() || detail.name,
          };
          return this.apartmentService.uploadLotteryPhoto(apartmentId, command);
        }),
        finalize(() => this.actionInFlight.set(false))
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Image uploaded',
          });
          this.resetUploadState();
          this.loadDetail(apartmentId);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to upload image',
            detail: 'Please try again.',
          });
        },
      });
  }

  protected onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.uploadTarget.set(undefined);
    if (!file) {
      this.resetUploadState();
      input.value = '';
      return;
    }

    this.uploadFile.set(file);
    this.uploadFileName.set(file.name);
    input.value = '';
    this.prepareUploadTarget(file);
  }

  private prepareUploadTarget(file: File) {
    this.uploadPreparing.set(true);
    this.apartmentService
      .getLotteryPhotoUploadTarget(this.apartmentId(), file.name, file.type)
      .pipe(finalize(() => this.uploadPreparing.set(false)))
      .subscribe({
        next: target => {
          this.uploadTarget.set(target);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to prepare upload link',
            detail: 'Please try again.',
          });
          this.uploadTarget.set(undefined);
        },
      });
  }

  private resetUploadState() {
    this.uploadFile.set(undefined);
    this.uploadTarget.set(undefined);
    this.uploadFileName.set('');
  }

  private loadDetail(apartmentId: string) {
    this.loadingPhotos.set(true);
    this.apartmentService
      .getLotteryPhotos(apartmentId)
      .pipe(finalize(() => this.loadingPhotos.set(false)))
      .subscribe({
        next: detail => {
          if (this.apartmentId() === apartmentId) {
            this.detail.set(detail);
          }
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Unable to load photos',
            detail: 'Please try again.',
          });
        },
      });
  }
}
